import { z } from 'zod'
import { ReportAccumulatorSchema, ReportFormattersSchema } from '../ratio/model'
import { TinyManagerTaskSchema, TinyProgramTaskSchema } from '../program'
import { type UserId } from '../ids'
import { TinyTeamSchema, TinyUserSchema } from '../entity'
import { TinyCompanySchema } from '../company'
import { type ReportResult } from './result'
import { type ReportKpiFilter, type ReportKpiGrouping } from './kpi'

export type TimeStamp = number
export type ISODateTime = `${number}-${number}-${number}`
export type DatePeriod = 'year' | 'quarter' | 'month' | 'week' | 'day' | 'hour'
/**
 * Examples: '-1m', '1y', '3d', '2w', '1h'
 */
export type DateExpression = `${number} ${DatePeriod}s` | `1 ${DatePeriod}` | `-1 ${DatePeriod}`
export type DateNow = 'now'
export type DateValue = TimeStamp | ISODateTime | DateExpression | DateNow

export type ReportDisplayType = 'table' | 'line' | 'bar' | 'pie'

export type ReportGrouping = 'user' | 'team' | 'company' | DatePeriod

export type ReportDefinition = {
  reportId: string
  title: string
  description?: string
  userId: UserId
  type: ReportDisplayType
  columns: ReportColumnDisplay[]
  grouping: ReportKpiGrouping
  // TODO: Update this filter to work across companies
  filter: ReportKpiFilter
  clientFilter: ClientFilter[]
  /** the datetime of the the last run if any */
  lastRun?: number
  lastResult?: ReportResult
}

export type ClientFilter = ClientNumberFilter | ClientStringFilter
export type ClientNumberFilter = {
  type: 'number'
  field: string
  min?: number
  max?: number
}
export type ClientStringFilter = {
  type: 'string'
  field: string
  operation: 'equals' | 'contains' | 'startsWith' | 'endsWith'
  value: string
}

export const ReportColumnTypeSchema = z.enum(['string', 'number', 'boolean', 'timestamp', 'group'])
export type ReportColumnType = z.infer<typeof ReportColumnTypeSchema>

export const ReportColumnVariantSchema = z.object({
  /** Shared key across related columns. Columns with the same id are switchable alternatives. */
  id: z.string().meta({ description: 'Groups switchable columns.' }),
  /** Which variant this column represents (e.g. 'contributor' or 'actor'). */
  option: z.string().meta({ description: 'Variant key, e.g. actor.' }),
})
export type ReportColumnVariant = z.infer<typeof ReportColumnVariantSchema>

export const ACCOUNT_FILTER_TYPES = ['location', 'customField', 'assignment'] as const
export const AccountFilterTypeSchema = z.enum(ACCOUNT_FILTER_TYPES)
export type AccountFilterType = z.infer<typeof AccountFilterTypeSchema>

export const ReportColumnKpiSchema = z.string()
export type ReportColumnKpi = z.infer<typeof ReportColumnKpiSchema>

export const ReportColumnTieBreakerSchema = z.object({
  label: z.string(),
  value: ReportColumnKpiSchema.meta({ description: 'KPI used when primary values tie.' }),
  type: ReportColumnTypeSchema,
  formatters: ReportFormattersSchema.array().optional(),
  direction: z.enum(['asc', 'desc']),
})
export type ReportColumnTieBreaker = z.infer<typeof ReportColumnTieBreakerSchema>

export const ReportColumnDefinitionSchema = z.object({
  /** The default label for this column. This can be overridden by the user. */
  label: z.string(),
  description: z.string(),
  type: ReportColumnTypeSchema,
  value: ReportColumnKpiSchema.meta({
    description: 'Pass unchanged to /report/kpi. groupBy returns the grouped entity.',
  }),
  width: z.number(),
  order: z.number(),
  accumulator: ReportAccumulatorSchema.optional(),
  formatters: ReportFormattersSchema.array().optional(),
  tieBreakers: ReportColumnTieBreakerSchema.array().optional(),
  group: z.string().optional(),
  default: z.boolean().optional(),
  /** Groups related columns that represent different perspectives on the same data point. */
  variant: ReportColumnVariantSchema.optional(),
  /** Older stored column keys that should resolve to `value`. */
  aliases: z.array(z.string()).optional(),
  accountFilterScope: AccountFilterTypeSchema.array().optional().meta({
    description:
      'Account filter types that narrow this column. location: states, cities, zipCodes and polygon. customField: customFields. assignment: users.',
  }),
})
export type ReportColumnDefinition = z.infer<typeof ReportColumnDefinitionSchema>

export const ReportLeaderboardColumnSchema = z.object({
  ...ReportColumnDefinitionSchema.shape,
  score: z.number(),
})
export type ReportLeaderboardColumn = z.infer<typeof ReportLeaderboardColumnSchema>

export const ReportColumnDisplaySchema = z.object({
  value: ReportColumnKpiSchema.meta({
    description: 'groupBy returns the grouped entity.',
  }),
  accumulator: ReportAccumulatorSchema.optional(),
})
export type ReportColumnDisplay = z.infer<typeof ReportColumnDisplaySchema>

export const ReportColumnValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  TinyUserSchema,
  TinyTeamSchema,
  TinyCompanySchema,
  TinyProgramTaskSchema,
  TinyManagerTaskSchema,
])
export type ReportColumnValue = z.infer<typeof ReportColumnValueSchema>
